import { CurrentPageSpanStoreManager } from '@wheely/web-tracing';
import { v4 as uuid } from 'uuid';

import { errorHandling } from './errorHandling';

type JournalValue = string | number | boolean | null | undefined;
type JournalData = Record<string, JournalValue>;
type JournalEntry = Record<string, string | number | boolean>;

interface FlowJournal {
  attemptId: string;
  /** Records a step: a Sentry breadcrumb plus an event on the current page span. */
  step: (name: string, data?: JournalData) => JournalEntry;
  /** Records a step and sends it to Sentry as a warning, with the steps before it attached. */
  report: (name: string, data?: JournalData) => void;
}

const addPageSpanEvent = (name: string, entry: JournalEntry) => {
  try {
    CurrentPageSpanStoreManager.getStore().getActiveSpan()?.addEvent(name, entry);
  } catch {
    // Tracing is not configured (local runs, tests): breadcrumbs are enough.
  }
};

/**
 * Step-by-step record of one run of a flow, enough to replay a support case.
 * Only ids, statuses, flags and timings belong here, never personal data.
 * Steps go to Sentry and to the page span: traces leave through this origin
 * and still arrive where sentry.io is out of reach.
 */
const createFlowJournal = (flow: string): FlowJournal => {
  const attemptId = uuid();
  const startedAt = performance.now();

  const step = (name: string, data: JournalData = {}) => {
    const entry: JournalEntry = {
      attempt_id: attemptId,
      ms: Math.round(performance.now() - startedAt),
    };

    Object.entries(data).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        entry[key] = value;
      }
    });

    errorHandling.addBreadcrumb(flow, name, entry);
    addPageSpanEvent(`${flow}.${name}`, entry);

    return entry;
  };

  const report = (name: string, data: JournalData = {}) => {
    const entry = step(name, data);
    const tags: Record<string, string> = { flow, step: name };

    Object.entries(entry).forEach(([key, value]) => {
      tags[key] = String(value);
    });

    errorHandling.captureWarning(`${flow}: ${name}`, { tags, extra: entry });
  };

  return { attemptId, step, report };
};

export type { FlowJournal, JournalData };
export { createFlowJournal };
